import { useEffect } from 'react';

/**
 * SeoHead — manages document head tags for SEO without react-helmet.
 *
 * Props:
 *   title         : Document <title>
 *   description   : meta description
 *   image         : OpenGraph image URL
 *   url           : Canonical URL (defaults to current location)
 *   jsonLd        : Object or array of objects → injected as <script type="application/ld+json">
 *   noindex       : if true, adds noindex,nofollow
 */
const SeoHead = ({ title, description, image, url, jsonLd, noindex = false }) => {
  useEffect(() => {
    const cleanup = [];

    const ensureMeta = (key, attr, content) => {
      if (!content) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        cleanup.push(() => el.parentNode?.removeChild(el));
      }
      el.setAttribute('content', content);
    };

    const ensureLink = (rel, href) => {
      if (!href) return;
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
        cleanup.push(() => el.parentNode?.removeChild(el));
      }
      el.setAttribute('href', href);
    };

    const finalUrl = url || (typeof window !== 'undefined' ? window.location.href : '');

    if (title) {
      const prev = document.title;
      document.title = title;
      cleanup.push(() => { document.title = prev; });
    }
    if (description) ensureMeta('description', 'name', description.slice(0, 320));
    if (noindex) ensureMeta('robots', 'name', 'noindex,nofollow');
    else ensureMeta('robots', 'name', 'index,follow');

    // OpenGraph
    ensureMeta('og:title', 'property', title);
    ensureMeta('og:description', 'property', description);
    ensureMeta('og:type', 'property', 'website');
    ensureMeta('og:url', 'property', finalUrl);
    ensureMeta('og:site_name', 'property', 'MAGERE Viandes');
    if (image) ensureMeta('og:image', 'property', image);
    // Twitter
    ensureMeta('twitter:card', 'name', image ? 'summary_large_image' : 'summary');
    ensureMeta('twitter:title', 'name', title);
    ensureMeta('twitter:description', 'name', description);
    if (image) ensureMeta('twitter:image', 'name', image);
    // Canonical
    ensureLink('canonical', finalUrl);

    // JSON-LD
    const jsonLdItems = Array.isArray(jsonLd) ? jsonLd : (jsonLd ? [jsonLd] : []);
    const scriptEls = jsonLdItems.map((data) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.text = JSON.stringify(data);
      s.dataset.seoHead = '1';
      document.head.appendChild(s);
      return s;
    });
    cleanup.push(() => scriptEls.forEach(s => s.parentNode?.removeChild(s)));

    return () => cleanup.forEach(fn => { try { fn(); } catch {} });
  }, [title, description, image, url, JSON.stringify(jsonLd), noindex]);

  return null;
};

export default SeoHead;
